/**
 * KULTA — Creative Enhancements JS
 */

/* ── Shared IntersectionObserver for scroll reveal ────────────────────── */
const kultaRevealObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('visible');
      kultaRevealObserver.unobserve(entry.target);
    }
  });
}, { threshold: 0.12 });

/* ── Respect users who prefer reduced motion ──────────────────────────── */
const kultaReduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── Init function scoped to a root element ───────────────────────────── */
function initKultaEnhancements(root) {

  /* ── 1. Hero: split title into chars ──────────────────────────────── */
  root.querySelectorAll('.hero-title').forEach((el) => {
    if (el.dataset.kultaSplitReady) return;
    el.dataset.kultaSplitReady = 'true';
    const accessibleTitle = el.textContent.trim();
    const words = accessibleTitle.split(' ');
    el.setAttribute('aria-label', accessibleTitle);
    el.innerHTML = '';
    words.forEach((word, wi) => {
      const wordSpan = document.createElement('span');
      wordSpan.className = 'word';
      wordSpan.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < word.length; i++) {
        const charSpan = document.createElement('span');
        charSpan.className = 'char';
        charSpan.textContent = word[i];
        wordSpan.appendChild(charSpan);
      }
      el.appendChild(wordSpan);
      if (wi < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
  });

  /* ── 2. Hero progress bar ─────────────────────────────────────────── */
  root.querySelectorAll('.hero').forEach((hero) => {
    if (hero.dataset.kultaProgressReady) return;
    hero.dataset.kultaProgressReady = 'true';
    const progressBar = document.createElement('div');
    progressBar.className = 'hero-progress';
    hero.appendChild(progressBar);

    function resetProgress() {
      progressBar.classList.remove('running');
      void progressBar.offsetWidth; // reflow
      progressBar.classList.add('running');
    }
    resetProgress();

    /* ── Hero: reset progress on slide change ─────────────────────── */
    const heroSlides = hero.querySelectorAll('.hero-slide');
    if (heroSlides.length > 1) {
      const mo = new MutationObserver(mutations => {
        mutations.forEach(m => {
          if (m.attributeName === 'class' && m.target.classList.contains('active')) {
            resetProgress();
          }
        });
      });
      heroSlides.forEach(s => mo.observe(s, { attributes: true }));
    }
  });

  /* ── 3. Magnetic buttons (rAF-throttled, rect cached per hover) ────── */
  if (!kultaReduceMotion) {
    root.querySelectorAll('.btn, .btn-primary, .btn-ghost').forEach(btn => {
      if (btn.dataset.kultaMagneticReady) return;
      btn.dataset.kultaMagneticReady = 'true';
      btn.classList.add('btn-magnetic');
      let rect = null, frame = null, lastEvent = null;
      btn.addEventListener('mouseenter', () => { rect = btn.getBoundingClientRect(); });
      btn.addEventListener('mousemove', (e) => {
        lastEvent = e;
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          if (!rect) rect = btn.getBoundingClientRect();
          const dx = lastEvent.clientX - (rect.left + rect.width / 2);
          const dy = lastEvent.clientY - (rect.top  + rect.height / 2);
          btn.style.transform = `translate(${dx * 0.22}px, ${dy * 0.22}px)`;
        });
      });
      btn.addEventListener('mouseleave', () => {
        if (frame) { cancelAnimationFrame(frame); frame = null; }
        rect = null;
        btn.style.transform = '';
      });
    });
  }

  /* ── 4. Material marquee — DESACTIVADO a pedido ───────────────────── */
  /* Se quitó la tira de materiales (Gold Vermeil · Sterling Silver…).   */
  /* Por si quedó alguna inyectada de una carga previa, la removemos.     */
  root.querySelectorAll('.material-marquee').forEach((el) => el.remove());

  /* ── 5. Mosaic: add data-num attribute ────────────────────────────── */
  const mosaicNums = {
    'collection-seaflare':    'No. 02',
    'collection-goldenfold':  'No. 03',
    'collection-starbound':   'No. 04',
    'collection-colorwave':   'No. 01',
    'collection-heatwave':    'No. 05',
    'collection-terra':       'No. 05',
    'collection-lunar':       'No. 06',
    'collection-dynasty':     'No. 07',
    'collection-midas':       'No. 09',
    'collection-glass':       'No. 08',
    'collection-nesting':     'No. 10',
    'collection-gilded':      'No. 11',
    'collection-starlit':     'No. 12',
  };
  root.querySelectorAll('.mosaic-tile').forEach(tile => {
    if (tile.dataset.kultaNumReady) return;
    tile.dataset.kultaNumReady = 'true';
    const href = tile.getAttribute('href') || '';
    const key = Object.keys(mosaicNums).find(k => href.includes(k));
    if (key) tile.setAttribute('data-num', mosaicNums[key]);
  });

  /* ── 6. Section watermarks ────────────────────────────────────────── */
  const watermarkNums = ['01', '02', '03', '04', '05'];
  root.querySelectorAll('main > .section').forEach((sec, i) => {
    if (sec.dataset.kultaWatermarkReady) return;
    sec.dataset.kultaWatermarkReady = 'true';
    if (watermarkNums[i]) {
      const wm = document.createElement('span');
      wm.className = 'section-watermark';
      wm.textContent = watermarkNums[i];
      sec.appendChild(wm);
    }
  });

  /* ── 7. Add reveal classes to sections, then observe them ─────────── */
  root.querySelectorAll('.section-header, .split-right').forEach((el, i) => {
    if (el.dataset.kultaRevealReady) return;
    el.dataset.kultaRevealReady = 'true';
    el.classList.add('reveal');
    const delay = ['reveal-delay-1','reveal-delay-2','reveal-delay-3','reveal-delay-4'][i % 4];
    el.classList.add(delay);
  });
  root.querySelectorAll('.reveal').forEach(el => kultaRevealObserver.observe(el));
}

/* ── Event listeners ──────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initKultaEnhancements(document);
});
document.addEventListener('shopify:section:load', (event) => {
  initKultaEnhancements(event.target);
});
